'use client'

/* eslint-disable react/hook-use-state */
import { arrayMove } from '@dnd-kit/sortable'
import { noop } from 'lodash'
import { createContext, ReactNode, useMemo, useState } from 'react'

import { UiState, useLatestCallback } from '@marketplace-web/shared/ui-state-util'
import { InitialMediaSelectData } from '@marketplace-web/item-upload/item-upload-data'
import {
  ImageOrientation,
  MediaSelectPhotoOrientation,
  MediaUploadPhotoModel,
} from '@marketplace-web/item-upload/media-upload-data'

import { MediaSelectContextType } from '../types/context'

export const MediaSelectContext = createContext<MediaSelectContextType>({
  photos: [],
  photoOrientations: [],
  photoTips: [],
  uiStateWithSetter: [UiState.Idle, noop],
  photosCount: 0,
  photoTempIds: [],
  removePhoto: noop,
  setPhotoOrientation: noop,
  changePhotoOrder: noop,
  addPhotos: noop,
  initialPhotosPresent: false,
})

export const MediaSelectContextProvider = ({
  children,
  initialMediaSelectData,
}: {
  children: ReactNode
  initialMediaSelectData?: InitialMediaSelectData
}) => {
  const [photos, setPhotos] = useState<Array<MediaUploadPhotoModel>>(
    initialMediaSelectData?.photos || [],
  )
  const [photoOrientations, setPhotoOrientations] = useState<Array<MediaSelectPhotoOrientation>>(
    initialMediaSelectData?.photoOrientations || [],
  )

  const uiStateWithSetter = useState(UiState.Idle)
  const photoTempIds = photos.reduce<Array<string>>((acc, photo) => {
    if (photo.tempUuid) acc.push(photo.tempUuid)

    return acc
  }, [])

  const addPhotos = useLatestCallback(
    (
      photosToBeAdded: Array<MediaUploadPhotoModel>,
      photoOrientationsToBeAdded: Array<MediaSelectPhotoOrientation>,
    ) => {
      if (!photosToBeAdded.length) return

      setPhotos(prev => [...prev, ...photosToBeAdded])
      setPhotoOrientations(prev => [...prev, ...photoOrientationsToBeAdded])
    },
  )

  const removePhoto = (photoId: number) => {
    setPhotos(prevPhotos => prevPhotos.filter(photo => photo.id !== photoId))
    setPhotoOrientations(prev => prev.filter(photo => photo.id !== photoId))
  }

  const changePhotoOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setPhotos(prev => arrayMove(prev, oldIndex, newIndex))
    setPhotoOrientations(prev => arrayMove(prev, oldIndex, newIndex))
  }

  const setPhotoOrientation = (photoOrientation: MediaSelectPhotoOrientation) => {
    setPhotoOrientations(prev =>
      prev.map(photo =>
        photo.id === photoOrientation.id
          ? { ...photo, orientation: photoOrientation.orientation || ImageOrientation.Degree0 }
          : photo,
      ),
    )
  }

  const providerValue = useMemo(
    () => ({
      photos,
      photoOrientations,
      uiStateWithSetter,
      photosCount: photos.length,
      photoTempIds,
      removePhoto,
      addPhotos,
      setPhotoOrientation,
      changePhotoOrder,
      photoTips: initialMediaSelectData?.photoTips || [],
      initialPhotosPresent: !!initialMediaSelectData?.photos?.length,
    }),
    [
      addPhotos,
      photoTempIds,
      photos,
      photoOrientations,
      uiStateWithSetter,
      initialMediaSelectData?.photoTips,
      initialMediaSelectData?.photos,
    ],
  )

  return <MediaSelectContext.Provider value={providerValue}>{children}</MediaSelectContext.Provider>
}
